import { App, Input, Modal } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { UnresolvedVendorErrorCodeResponseDto } from '@evimo/client/models';
import type { UnresolvedRemarkAction } from '~/app/components/errorManagement/UnresolvedVendorErrorCodeTab/types';
import { isConflictError } from '~/app/components/errorManagement/UnresolvedVendorErrorCodeTab/utils';
import { UnresolvedVendorErrorCodeService } from '~/app/services/errorManagement/unresolved-vendor-error-code.service';

async function invalidateUnresolvedAndCatalog(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ['unresolved-vendor-error-codes'],
    }),
    queryClient.invalidateQueries({
      queryKey: ['vendor-error-codes'],
    }),
  ]);
}

export function useUnresolvedRemarkActions() {
  const { t } = useTranslation('errorManagement');
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const remarkActionMutation = useMutation({
    mutationFn: ({
      id,
      action,
      remark,
      expectedVersion,
    }: {
      id: string;
      action: UnresolvedRemarkAction;
      remark: string;
      expectedVersion: number;
    }) => {
      const dto = { remark, expectedVersion };
      switch (action) {
        case 'resolve':
          return UnresolvedVendorErrorCodeService.resolve(id, dto);
        case 'ignore':
          return UnresolvedVendorErrorCodeService.ignore(id, dto);
        case 'unignore':
          return UnresolvedVendorErrorCodeService.unignore(id, dto);
      }
    },
    onSuccess: async (_record, variables) => {
      const successKey = (() => {
        switch (variables.action) {
          case 'resolve':
            return 'unresolved.messages.resolveSuccess';
          case 'ignore':
            return 'unresolved.messages.ignoreSuccess';
          case 'unignore':
            return 'unresolved.messages.unignoreSuccess';
        }
      })();
      message.success(t(successKey));
      await invalidateUnresolvedAndCatalog(queryClient);
    },
    onError: (error, variables) => {
      if (isConflictError(error)) {
        message.error(t('unresolved.messages.actionConflict'));
        return;
      }
      const failKey = (() => {
        switch (variables.action) {
          case 'resolve':
            return 'unresolved.messages.resolveFailed';
          case 'ignore':
            return 'unresolved.messages.ignoreFailed';
          case 'unignore':
            return 'unresolved.messages.unignoreFailed';
        }
      })();
      message.error(t(failKey));
    },
  });

  const openRemarkActionModal = (
    action: UnresolvedRemarkAction,
    record: UnresolvedVendorErrorCodeResponseDto,
  ) => {
    const titleKey = (() => {
      switch (action) {
        case 'resolve':
          return 'unresolved.actions.resolve';
        case 'ignore':
          return 'unresolved.actions.ignore';
        case 'unignore':
          return 'unresolved.actions.unignore';
      }
    })();
    const descriptionKey = (() => {
      switch (action) {
        case 'resolve':
          return 'unresolved.messages.resolveConfirmDescription';
        case 'ignore':
          return 'unresolved.messages.ignoreConfirmDescription';
        case 'unignore':
          return 'unresolved.messages.unignoreConfirmDescription';
      }
    })();

    let remark = '';
    Modal.confirm({
      title: t(titleKey),
      content: createElement(
        'div',
        null,
        createElement('p', { style: { marginBottom: 12 } }, t(descriptionKey)),
        createElement(Input.TextArea, {
          rows: 3,
          placeholder: t('unresolved.messages.remarkRequired'),
          onChange: (event) => {
            remark = event.target.value;
          },
        }),
      ),
      okText: t(titleKey),
      okButtonProps: action === 'ignore' ? { danger: true } : undefined,
      onOk: async () => {
        if (!remark.trim()) {
          message.error(t('unresolved.messages.remarkRequired'));
          return Promise.reject(new Error('REMARK_REQUIRED'));
        }
        try {
          await remarkActionMutation.mutateAsync({
            id: record.id,
            action,
            remark: remark.trim(),
            expectedVersion: record.version,
          });
        } catch (error) {
          if (error instanceof Error && error.message === 'REMARK_REQUIRED') {
            return Promise.reject(error);
          }
          return Promise.reject(error);
        }
      },
    });
  };

  return {
    isRemarkActionPending: remarkActionMutation.isPending,
    openRemarkActionModal,
  };
}
